/**
 * What we ask the extractor for on a police report, and how the answers are laid out.
 *
 * One list drives both: `fieldsToExtract` sent to the service, and the grouping the
 * panel renders. Keeping them together is what stops the two drifting apart -- a key
 * asked for but not described would land in "Other" and look like a bug.
 *
 * This lives in the frontend on purpose for now. The list is still being tuned against
 * real reports, and a constant here can be changed without a backend deploy. It moves
 * server-side once it settles.
 */

export const POLICE_REPORT_FIELD_GROUPS = [
    {
        key: "report",
        label: "Report",
        fields: [
            { key: "report_number", label: "Report number" },
            { key: "agency", label: "Agency" },
            { key: "officer_name", label: "Officer" },
            { key: "officer_badge", label: "Badge" },
            { key: "report_date", label: "Report date" },
        ],
    },
    {
        key: "incident",
        label: "Incident",
        fields: [
            { key: "incident_date", label: "Incident date" },
            { key: "incident_time", label: "Incident time" },
            { key: "location", label: "Location" },
            { key: "weather", label: "Weather" },
            { key: "road_conditions", label: "Road conditions" },
            { key: "contributing_factors", label: "Contributing factors" },
        ],
    },
    {
        key: "vehicles",
        label: "Vehicles",
        fields: [
            { key: "vehicle_1_year_make_model", label: "Vehicle 1" },
            { key: "vehicle_1_plate", label: "Vehicle 1 plate" },
            { key: "vehicle_1_vin", label: "Vehicle 1 VIN" },
            { key: "vehicle_1_damage", label: "Vehicle 1 damage" },
            { key: "vehicle_2_year_make_model", label: "Vehicle 2" },
            { key: "vehicle_2_plate", label: "Vehicle 2 plate" },
            { key: "vehicle_2_vin", label: "Vehicle 2 VIN" },
            { key: "vehicle_2_damage", label: "Vehicle 2 damage" },
        ],
    },
    {
        key: "people",
        label: "People",
        fields: [
            { key: "driver_1_name", label: "Driver 1" },
            { key: "driver_1_license_number", label: "Driver 1 licence" },
            { key: "driver_1_phone", label: "Driver 1 phone" },
            { key: "driver_1_insurance_carrier", label: "Driver 1 insurer" },
            { key: "driver_1_policy_number", label: "Driver 1 policy" },
            { key: "driver_2_name", label: "Driver 2" },
            { key: "driver_2_license_number", label: "Driver 2 licence" },
            { key: "driver_2_phone", label: "Driver 2 phone" },
            { key: "driver_2_insurance_carrier", label: "Driver 2 insurer" },
            { key: "driver_2_policy_number", label: "Driver 2 policy" },
            { key: "passengers", label: "Passengers" },
            { key: "witnesses", label: "Witnesses" },
            { key: "injuries", label: "Injuries" },
        ],
    },
    {
        key: "citations",
        label: "Citations & tow",
        fields: [
            { key: "citations_issued", label: "Citations issued" },
            { key: "tow_company", label: "Tow company" },
        ],
    },
    {
        key: "narrative",
        label: "Narrative",
        fields: [
            { key: "at_fault_party", label: "At-fault party" },
            { key: "narrative", label: "Officer narrative" },
        ],
    },
]

export const POLICE_REPORT_FIELDS = POLICE_REPORT_FIELD_GROUPS.reduce(
    (keys, group) => keys.concat(group.fields.map((field) => field.key)),
    []
)

export const POLICE_REPORT_CONTEXT = [
    "Dates as YYYY-MM-DD and times as 24-hour HH:MM.",
    "Plates and VINs uppercase, with no spaces.",
    "Use null for anything the report does not state -- never guess.",
    "narrative should be the officer's account verbatim, not a summary.",
].join(" ")

const LABELS_BY_KEY = POLICE_REPORT_FIELD_GROUPS.reduce((labels, group) => {
    group.fields.forEach((field) => {
        labels[field.key] = field.label
    })
    return labels
}, {})

function humanize(key) {
    return String(key).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
}

function readCell(cell) {
    // The service returns {value, confidence} per key, but a bare value is what comes
    // back when the model ignores the shape it was asked for. Both are read here so a
    // sloppy response degrades to a plain row instead of rendering "[object Object]".
    if (cell === null || cell === undefined) return null
    if (typeof cell === "object" && !Array.isArray(cell)) {
        return { value: cell.value, confidence: cell.confidence || null }
    }
    return { value: cell, confidence: null }
}

function displayValue(value) {
    if (value === null || value === undefined || value === "") return null
    if (Array.isArray(value)) return value.join(", ")
    if (typeof value === "object") return JSON.stringify(value)
    return String(value)
}

/**
 * Turn the service's flat structured_output into the panel's groups.
 *
 * Keys we never asked for are kept in an "Other" group rather than dropped -- the
 * model volunteering a tow yard phone number is a find, not noise, and silently
 * discarding it would be indistinguishable from the model missing it.
 */
export function groupExtractedFields(structuredOutput) {
    if (!structuredOutput || typeof structuredOutput !== "object") return []

    const seen = new Set()
    const groups = POLICE_REPORT_FIELD_GROUPS.map((group) => {
        const rows = []
        group.fields.forEach((field) => {
            const cell = readCell(structuredOutput[field.key])
            const value = cell && displayValue(cell.value)
            seen.add(field.key)
            if (value === null) return
            rows.push({ key: field.key, label: field.label, value, confidence: cell.confidence })
        })
        return { key: group.key, label: group.label, rows }
    }).filter((group) => group.rows.length > 0)

    const extraRows = []
    Object.keys(structuredOutput).forEach((key) => {
        if (seen.has(key)) return
        const cell = readCell(structuredOutput[key])
        const value = cell && displayValue(cell.value)
        if (value === null) return
        extraRows.push({ key, label: LABELS_BY_KEY[key] || humanize(key), value, confidence: cell.confidence })
    })
    if (extraRows.length > 0) groups.push({ key: "other", label: "Other", rows: extraRows })

    return groups
}

export function asCopyText(groups) {
    return groups
        .map((group) => `${group.label.toUpperCase()}\n` + group.rows.map((r) => `${r.label}: ${r.value}`).join("\n"))
        .join("\n\n")
}
